'use client';

import {
  useClientTranslation,
  interpolateTranslation,
} from '@bts-web/utils-lokalise';
import { TFunction } from 'i18next';
import { getAppConfig } from '@bts-web/core-app-config';
import { useModalDrawerContext } from '../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { getTradeAssetSelectorTranslations } from '../../ChangeCurrencyOrSwap/changeCurrencyUtils';
import { useFiatWallet } from './FiatWalletContext.use-client';
import { FiatWalletSkeleton } from './FiatWalletSkeleton';
import { WalletCell } from './WalletCell';
import { DepositNeededCell } from './DepositNeededCell';
import { useFeatureFlag } from '../../../statsig/sdk/provider';

interface FiatWalletControllerProps {
  amountInput: string;
  locale: string;
  currency: string;
}

export const FiatWalletController = ({
  amountInput,
  currency,
  locale,
}: FiatWalletControllerProps) => {
  const { isLoading, hasError, data } = useFiatWallet();

  const feature_enableFiatTransfersPartialDeposit = useFeatureFlag(
    'feature_enableFiatTransfersPartialDeposit',
  );

  const { openDrawer } = useModalDrawerContext();

  const { t } = useClientTranslation();

  const { bankLogo: BankLogo } = getAppConfig();

  const rawTranslations = getTradeAssetSelectorTranslations(t as TFunction);

  const translations = {
    ...rawTranslations,
    fiatWalletLabel: interpolateTranslation(rawTranslations.fiatWalletLabel, {
      fiatsymbol: currency,
    }),
  };

  if (isLoading) {
    return <FiatWalletSkeleton />;
  }

  if (hasError) {
    return null;
  }

  const availableAmount = Number(data?.walletAmount || 0);

  const showCashOption =
    availableAmount > 0 &&
    availableAmount >= Number(amountInput.replace(',', '.'));

  if (showCashOption) {
    return (
      <WalletCell
        walletAmount={data?.walletAmount || '0'}
        locale={locale}
        currency={currency}
        translations={translations}
        openDrawer={openDrawer}
      />
    );
  }

  return feature_enableFiatTransfersPartialDeposit ? (
    <DepositNeededCell
      walletAmount={data?.walletAmount || '0'}
      depositAmount={String(
        Number(amountInput.replace(',', '.')) - availableAmount,
      )}
      locale={locale}
      currency={currency}
      appLogo={BankLogo ? <BankLogo size={16} /> : null}
      accountNumber={data?.accountNumber ?? ''}
      bankAccountHolder={data?.bankAccountHolder ?? ''}
      bankName={data?.bankName ?? ''}
      openDrawer={openDrawer}
      translations={translations}
      hasWalletBalance={availableAmount > 0}
    />
  ) : (
    <DepositNeededCell
      walletAmount={data?.walletAmount || '0'}
      depositAmount={amountInput}
      locale={locale}
      currency={currency}
      appLogo={BankLogo ? <BankLogo size={16} /> : null}
      accountNumber={data?.accountNumber ?? ''}
      bankAccountHolder={data?.bankAccountHolder ?? ''}
      bankName={data?.bankName ?? ''}
      openDrawer={openDrawer}
      translations={translations}
      hasWalletBalance={false}
    />
  );
};
